// catalogApi — danh mục gói MỘT MỐI (bảng products, quyết định 0086).
//
// Thay cho cặp "bảng tĩnh PRODUCT_CATEGORIES + thiết lập custom_products" ở
// mọi màn: bộ chọn gói (form đơn), trang Sản phẩm, tab Sản phẩm (Trang phục),
// Định mức ưu đãi, tên gói ở màn Sửa hình. Thiết lập `custom_products` đã
// nghỉ — máy chủ trả 410 khi ghi vào khoá đó.
//
// Lưới đỡ: API lỗi → trả bảng tĩnh (đúng bảng cũ) để form đơn không bao giờ
// trắng gói, nhưng báo `isFallback` để màn quản lý hiện cảnh báo + khoá sửa.
// Không nuốt lỗi câm: `error` của react-query vẫn được trả ra.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { PRODUCT_CATEGORIES, type SubProduct } from "./constants";
import type { PickerProduct } from "./packageOptions";

export type CatalogProduct = components["schemas"]["CatalogProduct"];
export type CatalogBrand = components["schemas"]["CatalogBrand"];
export type CatalogLine = components["schemas"]["CatalogLine"];
export type CatalogProductInput = components["schemas"]["CatalogProductInput"];
export type CatalogProductUpdate = components["schemas"]["CatalogProductUpdate"];
export type CatalogBrandsResponse = components["schemas"]["CatalogBrandsResponse"];

export const CATALOG_KEY = ["catalog", "products"] as const;
/** Khoá gốc của kho concept/trang phục — chép từ lookbook/api để tránh import vòng. */
const LOOKBOOK_KEY = ["lookbook"] as const;
export const BRANDS_KEY = ["catalog", "brands"] as const;

/**
 * LƯỚI ĐỠ khi danh sách thương hiệu chưa tải xong — KHÔNG phải nguồn chân lý.
 *
 * Nguồn thật là cột is_active của bảng brands, đọc qua useSellingBrands().
 * Lớp 1 ghi cứng hai mã ở đây và ở router.go; CEO chốt 02/09/2026 (4b) mở bán
 * GNCA, nên ghi cứng là phải phát hành lại cả hai đầu mỗi lần mở một thương
 * hiệu. Giữ hằng này chỉ để khoảnh khắc đang tải không hiện danh sách rỗng.
 */
export const SELLING_BRANDS: readonly string[] = ["gnat", "gnpf"];

/**
 * Mã thương hiệu ĐANG bán, lấy từ bảng brands. Chưa tải xong / API lỗi thì rơi
 * về SELLING_BRANDS thay vì trả rỗng: rỗng làm bộ chọn gói trắng trơn, trông
 * y hệt "hệ thống hỏng".
 */
export function useSellingBrands(): readonly string[] {
  const { data } = useCatalogBrands();
  const active = data?.brands.filter((b) => b.isActive).map((b) => b.code);
  return active ?? SELLING_BRANDS;
}

const BRAND_NAMES: Record<string, string> = {
  gnat: "Gạo Nâu Chụp Ảnh Thẻ",
  gnpf: "Gạo Nâu Profile",
};

/** Dòng → thương hiệu của danh mục tĩnh (chỉ 3 dòng cũ). Ảnh thẻ thuộc GNAT. */
export function staticBrandOf(lineCode: string): string {
  return lineCode === "idphoto" ? "gnat" : "gnpf";
}

/**
 * Bảng tĩnh chuyển sang khuôn API — LƯỚI ĐỠ khi API lỗi, và dữ liệu cho bài
 * kiểm giao diện. Cùng mã/giá/tên với PRODUCT_CATEGORIES (Task 1 đã đồng bộ
 * bảng đó với seed 0192). Không tự memo: 3 nhóm × ≤10 gói, tính lại rẻ.
 */
export function staticCatalogProducts(): CatalogProduct[] {
  const out: CatalogProduct[] = [];
  let order = 0;
  for (const cat of Object.values(PRODUCT_CATEGORIES)) {
    for (const sp of cat.subProducts) {
      order += 10;
      const brandCode = staticBrandOf(cat.id);
      out.push({
        code: sp.id,
        brandCode,
        brandName: BRAND_NAMES[brandCode] ?? brandCode,
        lineCode: cat.id,
        lineName: cat.name,
        name: sp.name,
        description: "",
        imageIllustrative: false,
        tier: "",
        basePrice: sp.price,
        editPhotos: sp.editPhotos,
        durationMin: sp.durationMin ?? 0,
        people: 0,
        // Bảng tĩnh chỉ có 3 dòng GNPF/GNAT — không dòng nào bật cờ số người,
        // nên hai ô này luôn "chưa khai" ở lưới đỡ.
        makeupPeople: null,
        prints: 0,
        hasMakeup: sp.name.includes("(có make)"),
        concepts: sp.concepts ?? [],
        purposes: sp.purposes ?? [],
        publicHidden: false,
        isActive: true,
        source: "gnpf_static",
        sortOrder: order,
        updatedAt: "1970-01-01T00:00:00Z",
      });
    }
  }
  return out;
}

const STATIC_FALLBACK: readonly CatalogProduct[] = staticCatalogProducts();

async function fetchCatalog(): Promise<CatalogProduct[]> {
  const { data, error } = await api.GET("/products", {
    params: { query: { includeInactive: true } },
  });
  // Kiểm cả `data.items` chứ không chỉ `data`: đáp ứng thiếu khoá `items`
  // (proxy trả khung rỗng, bản máy chủ cũ) làm hàm này trả về undefined, và
  // react-query nổ "Query data cannot be undefined" ở console thay vì đi vào
  // nhánh lỗi. Ném lỗi ở đây mới bật được bảng tĩnh dự phòng kèm cảnh báo.
  if (error || !data?.items) throw new Error(serverErrorMessage(error, "Không đọc được danh mục gói"));
  return data.items;
}

export type ProductCatalog = {
  /** Gói của MỌI thương hiệu, kể cả ngừng bán (includeInactive=true). */
  products: readonly CatalogProduct[];
  /** true = đang hiện bảng tĩnh dự phòng vì API chưa về / lỗi. */
  isFallback: boolean;
  isLoading: boolean;
  error: Error | null;
};

/**
 * Danh mục gói một mối. Trả bảng tĩnh khi API lỗi — màn quản lý phải nhìn
 * `isFallback` để cảnh báo + khoá nút sửa; form đơn cứ dùng `products`.
 */
export function useProductCatalog(): ProductCatalog {
  const q = useQuery({
    queryKey: CATALOG_KEY,
    queryFn: fetchCatalog,
    staleTime: 60_000,
  });
  return {
    products: q.data ?? STATIC_FALLBACK,
    isFallback: q.data === undefined,
    isLoading: q.isLoading,
    error: q.error,
  };
}

/**
 * Mã thương hiệu → tên ngắn để hiển thị.
 *
 * Luôn trả nhãn cho cả thương hiệu ĐÃ TẮT: nhãn là để gọi tên một bản ghi đã
 * tồn tại, không phải để mời chọn. Mã lạ (đơn cũ chưa gắn thương hiệu, gom
 * dưới ô "(chưa xác định)") trả về nguyên văn thay vì chuỗi rỗng — thà thấy mã
 * còn hơn thấy ô trống không biết là gì.
 */
export function useBrandLabel(): (code: string | null | undefined) => string {
  const { data } = useCatalogBrands();
  return useMemo(() => {
    const byCode = new Map((data?.brands ?? []).map((b) => [b.code, b.shortName]));
    return (code: string | null | undefined) => (code ? (byCode.get(code) ?? code) : "");
  }, [data]);
}

/**
 * Mã dòng sản phẩm → tên tiếng Việt (`idphoto` → "Ảnh thẻ").
 *
 * Nguồn là bảng `product_lines` qua chính lời gọi của useBrandLabel, nên thêm
 * một dòng ở CSDL là mọi màn dùng hook này gọi đúng tên ngay — KHÔNG phải bảng
 * ghi cứng như CATEGORY_TABS, vốn chỉ biết 3 dòng cũ và trả mã thô cho 11 dòng
 * còn lại (`ca_nhan`, `travel_glow`…).
 *
 * Mã lạ trả về NGUYÊN VĂN, cùng luật với useBrandLabel: thà thấy mã còn hơn
 * thấy ô trống không biết dòng tiền đó là gì.
 */
export function useLineLabel(): (code: string | null | undefined) => string {
  const { data } = useCatalogBrands();
  return useMemo(() => {
    const byCode = new Map((data?.lines ?? []).map((l) => [l.code, l.name]));
    return (code: string | null | undefined) => (code ? (byCode.get(code) ?? code) : "");
  }, [data]);
}

/**
 * Dòng sản phẩm có bật "được xếp là nhận ca" không (quyết định 0234 — hôm nay
 * là ảnh thẻ). Bật thì người có tên trong ê-kíp coi như ĐÃ nhận ca: không bày
 * nút "Nhận ca", chip không treo "Chờ nhận ca".
 *
 * Hỏi CỜ của dòng, không hỏi tên nhóm — gương của `catalog.CapCrewAcceptOnAssign`
 * phía máy chủ. Chưa đọc xong danh mục, hoặc dòng không khai cờ → `false`: đi
 * đường cũ, còn nút Nhận ca. Bấm thừa nút đó vô hại (máy chủ chỉ ghi dấu, không
 * đẩy trạng thái đơn của dòng bật cờ).
 */
export function useCrewAcceptOnAssign(): (lineCode: string | null | undefined) => boolean {
  const { data } = useCatalogBrands();
  return useMemo(() => {
    const on = new Set((data?.lines ?? []).filter((l) => l.crewAcceptOnAssign).map((l) => l.code));
    return (lineCode: string | null | undefined) => (lineCode ? on.has(lineCode) : false);
  }, [data]);
}

export function useCatalogBrands() {
  return useQuery({
    queryKey: BRANDS_KEY,
    queryFn: async (): Promise<CatalogBrandsResponse> => {
      const { data, error } = await api.GET("/products/brands");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đọc được thương hiệu"));
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CatalogProductInput): Promise<CatalogProduct> => {
      const { data, error } = await api.POST("/products", { body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tạo được gói"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG_KEY });
      // Ảnh đại diện gói Chụp Ảnh soi sang ảnh #1 concept (QĐ 0111).
      void qc.invalidateQueries({ queryKey: LOOKBOOK_KEY });
    },
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      code,
      body,
    }: {
      code: string;
      body: CatalogProductUpdate;
    }): Promise<CatalogProduct> => {
      const { data, error } = await api.PUT("/products/{code}", {
        params: { path: { code } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được gói"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG_KEY });
      // Ảnh đại diện gói Chụp Ảnh soi sang ảnh #1 concept (QĐ 0111).
      void qc.invalidateQueries({ queryKey: LOOKBOOK_KEY });
    },
  });
}

export function useSetProductActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      code,
      active,
    }: {
      code: string;
      active: boolean;
    }): Promise<CatalogProduct> => {
      const { data, error } = await api.PATCH("/products/{code}/active", {
        params: { path: { code } },
        body: { active },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đổi được trạng thái bán"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG_KEY });
      // Ảnh đại diện gói Chụp Ảnh soi sang ảnh #1 concept (QĐ 0111).
      void qc.invalidateQueries({ queryKey: LOOKBOOK_KEY });
    },
  });
}

/** Gói của một dòng, giữ nguyên thứ tự máy chủ trả (sortOrder, code). */
export function productsByLine(
  products: readonly CatalogProduct[],
  lineCode: string,
): CatalogProduct[] {
  return products.filter((p) => p.lineCode === lineCode);
}

/**
 * Gói ĐANG BÁN của thương hiệu đang bán — đầu vào bộ chọn gói + Định mức.
 *
 * `sellingBrands` truyền vào từ useSellingBrands(); không truyền thì dùng lưới
 * đỡ. Cố ý để tham số chứ không gọi hook bên trong: hàm này còn được gọi trong
 * bài kiểm và ngoài cây React.
 */
export function sellableProducts(
  products: readonly CatalogProduct[],
  sellingBrands: readonly string[] = SELLING_BRANDS,
): CatalogProduct[] {
  return products.filter((p) => p.isActive && sellingBrands.includes(p.brandCode));
}

/**
 * Khuôn cho buildPackageOptions. Bỏ purposes/concepts RỖNG: bộ chọn hiểu
 * "không khai báo" = không giới hạn concept / không hỏi mục đích — đúng nghĩa
 * gói tĩnh cũ không có hai trường này.
 */
export function toPickerProduct(p: CatalogProduct): PickerProduct {
  return {
    id: p.code,
    name: p.name,
    price: p.basePrice,
    // durationMin phải đi cùng gói tới form tạo đơn: OrderCreateForm gọi
    // shootDurationMin(category, p.durationMin) để đặt thời lượng buổi chụp
    // (SCH-01). Bỏ trường này ra thì 8 gói CV rơi về 30' mặc định của nhóm
    // Ảnh thẻ thay vì 60' — TypeScript KHÔNG bắt được vì trường là tuỳ chọn.
    ...(p.durationMin > 0 ? { durationMin: p.durationMin } : {}),
    // Số người của gói đi cùng xuống form đơn để điền sẵn ô "Số người trong
    // buổi" thay vì bắt lễ tân gõ lại con số gói đã nói. 0 = gói chưa khai,
    // bỏ hẳn khoá đi để form rơi về ô nhập tay (xem OrderHeadcountField).
    ...(p.people > 0 ? { people: p.people } : {}),
    // null = chưa khai; 0 là lời khai thật ("gói không có ai trang điểm") nên
    // phải lọt qua đây, không dùng phép so `> 0` như `people` ở trên.
    ...(p.makeupPeople != null ? { makeupPeople: p.makeupPeople } : {}),
    ...(p.purposes.length > 0 ? { purposes: p.purposes } : {}),
    ...(p.concepts.length > 0 ? { concepts: p.concepts } : {}),
    // editPhotos + imageUrl chỉ để HIỂN THỊ ở thẻ xem trước gói của form đơn —
    // không tham gia vào giá hay dữ liệu ghi xuống đơn.
    ...(p.editPhotos > 0 ? { editPhotos: p.editPhotos } : {}),
    // Ảnh bìa + hạng + cờ minh hoạ cho lưới chọn gói (QĐ 0130). Chuỗi rỗng
    // bỏ hẳn, cùng luật với hai trường trên: "không khai" = không có.
    ...(p.imageUrl ? { imageUrl: p.imageUrl } : {}),
    ...(p.tier ? { tier: p.tier } : {}),
    ...(p.imageIllustrative ? { imageIllustrative: true } : {}),
  };
}

/** Khuôn cho ProductCard / Định mức (SubProduct của bảng tĩnh). */
export function toSubProduct(p: CatalogProduct): SubProduct {
  return {
    id: p.code,
    name: p.name,
    price: p.basePrice,
    editPhotos: p.editPhotos,
    ...(p.concepts.length > 0 ? { concepts: p.concepts } : {}),
    ...(p.durationMin > 0 ? { durationMin: p.durationMin } : {}),
    ...(p.people > 0 ? { people: p.people } : {}),
    ...(p.makeupPeople != null ? { makeupPeople: p.makeupPeople } : {}),
    ...(p.purposes.length > 0 ? { purposes: p.purposes } : {}),
  };
}

/** Mã gói → tên, cho màn chỉ cần dịch mã (Sửa hình). Đủ cả gói ngừng bán. */
export function useProductNameMap(): Record<string, string> {
  const { products } = useProductCatalog();
  return useMemo(() => {
    const out: Record<string, string> = {};
    for (const p of products) out[p.code] = p.name;
    return out;
  }, [products]);
}
